import {useCallback, useMemo, useState} from 'react';
import {tt} from '../../../common/getTranslations';
import {twMerge} from '../../../common/twMerge';
import {Icon} from '../../atoms/Icon/Icon';
import {IconType} from '../../atoms/Icon/IconType.enum';
import {Text} from '../../atoms/Text/Text';
import {Dialog} from '../../organisms/Dialog';
import {SelectV2Props} from './SelectV2Props.interface';

export function SelectV2({
  id,
  value,
  onChange,
  options,
  placeholder,
  disabled,
  status = 'normal',
  className,
  isMobile,
  ...props
}: SelectV2Props) {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const selectedOption = useMemo(() => {
    if (!value || !options) {
      return null;
    }
    return options.find(option => option.value === value);
  }, [value, options]);

  const onClickOption = useCallback(
    (_value: string) => {
      value !== _value && onChange?.(_value);
      setIsOpen(false);
    },
    [onChange, value]
  );

  return (
    <>
      <div
        onClick={() => !disabled && setIsOpen(true)}
        className={twMerge(
          'flex w-full h-48pxr items-center rounded-8pxr border-1 border-solid border-theme-transparent-30 px-18pxr cursor-pointer',
          status === 'error' &&
            'border-static-point-red bg-[rgb(var(--colors-static-point-red)_/_5%)]',
          disabled &&
            'border-theme-transparent-20 text-theme-solid-20 cursor-not-allowed',
          className
        )}
        {...props}
      >
        <select
          id={id}
          hidden
          onFocus={() => !disabled && setIsOpen(true)}
          onBlur={() => !disabled && setIsOpen(false)}
        />
        <div className="w-full font-medium2 ">
          {selectedOption ? (
            <Text
              className={twMerge(
                'text-theme-solid-100',
                disabled && 'text-theme-solid-20'
              )}
            >
              {tt(selectedOption)}
            </Text>
          ) : placeholder ? (
            <Text
              className={twMerge(
                'text-theme-solid-40',
                disabled && 'text-theme-solid-20'
              )}
            >
              {placeholder}
            </Text>
          ) : null}
        </div>
        {!disabled && (
          <Icon
            className="ml-4pxr"
            aria-hidden={false}
            type={isOpen ? IconType.ButtonArrowUp : IconType.ButtonArrowDown}
          />
        )}
      </div>
      <Dialog
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        isMobile={isMobile}
      >
        <Dialog.Dimmed />
        <Dialog.List className="space-y-0">
          {options?.map((option, index) => (
            <Dialog.Option
              onClick={() => onClickOption(option.value)}
              isChecked={value === option.value}
              key={`${index}-${option.value}`}
            >
              {tt(option)}
            </Dialog.Option>
          ))}
        </Dialog.List>
      </Dialog>
    </>
  );
}
